import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MoveUp, MoveDown, GripVertical } from "lucide-react";
import { Habit } from "@/context/SupabaseHabitContext";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ReorderHabitsDialogProps {
  habits: Habit[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReorder: (reorderedHabits: { id: string; order: number }[]) => void;
}

interface SortableHabitItemProps {
  habit: Habit;
  index: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  saving: boolean;
}

function SortableHabitItem({ 
  habit, 
  index, 
  onMoveUp, 
  onMoveDown, 
  canMoveUp, 
  canMoveDown, 
  saving 
}: SortableHabitItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: habit.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between rounded-lg border p-2 bg-card ${
        isDragging ? 'opacity-50 shadow-lg' : ''
      }`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-accent rounded transition-colors"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} className="text-muted-foreground" />
        </div>
        <div 
          className="w-3 h-3 rounded-sm flex-shrink-0" 
          style={{ backgroundColor: habit.color.filled }}
        />
        <span className="font-medium text-sm truncate">
          {habit.name}
          {habit.active === false && (
            <span className="ml-2 text-xs text-muted-foreground">(Paused)</span>
          )}
        </span>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button
          variant="outline"
          size="icon"
          onClick={onMoveUp}
          disabled={!canMoveUp || saving}
          className="h-7 w-7"
          title="Move up"
        >
          <MoveUp size={14} />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={onMoveDown}
          disabled={!canMoveDown || saving}
          className="h-7 w-7"
          title="Move down"
        >
          <MoveDown size={14} />
        </Button>
      </div>
    </div>
  );
}

export default function ReorderHabitsDialog({
  habits,
  open,
  onOpenChange,
  onReorder,
}: ReorderHabitsDialogProps) {
  const [localHabits, setLocalHabits] = useState<Habit[]>(() => [...habits]);
  const [saving, setSaving] = useState(false);

  // Sync localHabits with habits prop when it changes
  useEffect(() => {
    setLocalHabits([...habits]);
  }, [habits]);

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Reset local habits when dialog opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      console.log("Opening dialog with habits:", habits);
      setLocalHabits([...habits]);
    }
    onOpenChange(newOpen);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setLocalHabits((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const moveHabit = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= localHabits.length) return;

    const newHabits = [...localHabits];
    const temp = newHabits[index];
    newHabits[index] = newHabits[newIndex];
    newHabits[newIndex] = temp;
    setLocalHabits(newHabits);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const reorderedHabits = localHabits.map((habit, index) => ({
        id: habit.id,
        order: index,
      }));
      await onReorder(reorderedHabits);
      toast.success("Habit order updated");
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving habit order:", error);
      toast.error("Failed to update habit order");
    } finally {
      setSaving(false);
    }
  };

  console.log("Current localHabits:", localHabits);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Reorder Habits</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Drag habits to reorder them, or use the arrow buttons
          </p>
        </DialogHeader>
        
        <ScrollArea className="h-[50vh] pr-4">
          {localHabits.length === 0 ? (
            <div className="text-center text-muted-foreground py-4">
              No habits to reorder
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localHabits.map(habit => habit.id)}
                strategy={verticalListSortingStrategy}
              >
            <div className="space-y-2">
              {localHabits.map((habit, index) => (
                    <SortableHabitItem
                  key={habit.id}
                      habit={habit}
                      index={index}
                      onMoveUp={() => moveHabit(index, "up")}
                      onMoveDown={() => moveHabit(index, "down")}
                      canMoveUp={index > 0}
                      canMoveDown={index < localHabits.length - 1}
                      saving={saving}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 